import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  CheckIcon,
  StarIcon,
  CogIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { NestedConfigField } from './NestedConfigField';
import { validateObjectField, type ValidationResult } from '../../utils/portfolioValidation';

interface WizardStep {
  id: string;
  title: string;
  description: string;
  category: string;
  fields: string[];
  icon: React.ComponentType<{ className?: string }>;
  validation?: (values: any) => string | null;
}

interface ConfigWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (configs: any) => void;
  currentConfigs?: any;
}

// Configuration fields that match database validation rules exactly
const configurationFields = {
  lifecycle: {
    trade_count_thresholds: {
      value: { initialization: 30, incubation: 50, maturity: 200 },
      default_value: { initialization: 30, incubation: 50, maturity: 200 },
      description: "Minimum trade count thresholds for lifecycle progression",
      validation_rules: {
        type: "object",
        properties: {
          initialization: { type: "number", min: 1, max: 1000 },
          incubation: { type: "number", min: 1, max: 1000 },
          maturity: { type: "number", min: 1, max: 1000 }
        },
        required_fields: ["initialization", "incubation", "maturity"]
      },
      has_user_override: false,
      display_unit: "trades",
      unit_type: "count"
    },
    allocation_caps: {
      value: { initialization: 3, incubation: 5, maturity: 8, legacy: 10 },
      default_value: { initialization: 3, incubation: 5, maturity: 8, legacy: 10 },
      description: "Maximum allocation percentages for strategies at different lifecycle stages",
      validation_rules: {
        type: "object",
        properties: {
          initialization: { type: "percent", min: 0.1, max: 50 },
          incubation: { type: "percent", min: 0.1, max: 50 },
          maturity: { type: "percent", min: 0.1, max: 50 },
          legacy: { type: "percent", min: 0.1, max: 50 }
        },
        required_fields: ["initialization", "incubation", "maturity", "legacy"]
      },
      has_user_override: false,
      display_unit: "percent",
      unit_type: "percentage"
    }
  },
  account_moves: {
    hierarchy_behavior: {
      value: {
        mode: "same_broker_only",
        enable_promotions: true,
        enable_demotions: true,
        enable_swaps: true,
        cross_broker_threshold: 2.0
      },
      default_value: {
        mode: "same_broker_only",
        enable_promotions: true,
        enable_demotions: true,
        enable_swaps: true,
        cross_broker_threshold: 2.0
      },
      description: "Controls how the portfolio recommendation engine handles account promotions and demotions across different brokers",
      validation_rules: {
        type: "object",
        properties: {
          mode: { type: "string", enum: ["disabled", "same_broker_only", "cross_broker"] },
          enable_promotions: { type: "boolean" },
          enable_demotions: { type: "boolean" },
          enable_swaps: { type: "boolean" },
          cross_broker_threshold: { type: "number", min: 0.1, max: 10 }
        },
        required_fields: ["mode", "enable_promotions", "enable_demotions", "enable_swaps", "cross_broker_threshold"]
      },
      has_user_override: false,
      display_unit: null,
      unit_type: "enum"
    },
    promotion_thresholds: {
      value: { Initialization: 0, Incubation: 0.5, Maturity: 1, Legacy: 1.5 },
      default_value: { Initialization: 0, Incubation: 0.5, Maturity: 1, Legacy: 1.5 },
      description: "Performance thresholds required for strategy promotion to next lifecycle stage",
      validation_rules: {
        type: "object",
        properties: {
          Initialization: { type: "number", min: -5, max: 10 },
          Incubation: { type: "number", min: -5, max: 10 },
          Maturity: { type: "number", min: -5, max: 10 },
          Legacy: { type: "number", min: -5, max: 10 }
        },
        required_fields: ["Initialization", "Incubation", "Maturity", "Legacy"]
      },
      has_user_override: false,
      display_unit: "ROT",
      unit_type: "roi"
    },
    demotion_thresholds: {
      value: { Initialization: -0.5, Incubation: -0.2, Maturity: -0.1, Legacy: 0 },
      default_value: { Initialization: -0.5, Incubation: -0.2, Maturity: -0.1, Legacy: 0 },
      description: "Performance thresholds that trigger strategy demotion to previous lifecycle stage",
      validation_rules: {
        type: "object",
        properties: {
          Initialization: { type: "number", min: -10, max: 5 },
          Incubation: { type: "number", min: -10, max: 5 },
          Maturity: { type: "number", min: -10, max: 5 },
          Legacy: { type: "number", min: -10, max: 5 }
        },
        required_fields: ["Initialization", "Incubation", "Maturity", "Legacy"]
      },
      has_user_override: false,
      display_unit: "ROT",
      unit_type: "roi"
    }
  },
  stability: {
    deviation_thresholds: {
      value: { win_rate_red: 10, win_rate_orange: 5, profit_factor_red: 35, profit_factor_orange: 20 },
      default_value: { win_rate_red: 10, win_rate_orange: 5, profit_factor_red: 35, profit_factor_orange: 20 },
      description: "Performance deviation thresholds for risk assessment",
      validation_rules: {
        type: "object",
        properties: {
          win_rate_red: { type: "percent", min: 1, max: 100 },
          win_rate_orange: { type: "percent", min: 1, max: 100 },
          profit_factor_red: { type: "percent", min: 1, max: 100 },
          profit_factor_orange: { type: "percent", min: 1, max: 100 }
        },
        required_fields: ["win_rate_red", "win_rate_orange", "profit_factor_red", "profit_factor_orange"]
      },
      has_user_override: false,
      display_unit: "percent",
      unit_type: "percentage"
    }
  },
  allocation: {
    performance_bounds: {
      value: { multiplier_min: 0.5, multiplier_max: 1.5, underperformer_threshold: 0, strong_performer_threshold: 3 },
      default_value: { multiplier_min: 0.5, multiplier_max: 1.5, underperformer_threshold: 0, strong_performer_threshold: 3 },
      description: "Performance-based allocation adjustment bounds",
      validation_rules: {
        type: "object",
        properties: {
          multiplier_min: { type: "number", min: 0.1, max: 2 },
          multiplier_max: { type: "number", min: 0.1, max: 2 },
          underperformer_threshold: { type: "number", min: -10, max: 10 },
          strong_performer_threshold: { type: "number", min: -10, max: 10 }
        },
        required_fields: ["multiplier_min", "multiplier_max", "underperformer_threshold", "strong_performer_threshold"]
      },
      has_user_override: false,
      display_unit: "mixed",
      unit_type: "mixed"
    }
  },
  scoring: {
    component_weights: {
      value: { performance_max: 40, regime_fit_max: 20, stability_max: 30, lifecycle_max: 10 },
      default_value: { performance_max: 40, regime_fit_max: 20, stability_max: 30, lifecycle_max: 10 },
      description: "Relative weights for scoring components (must sum to 100)",
      validation_rules: {
        type: "object",
        properties: {
          performance_max: { type: "number", min: 1, max: 100 },
          regime_fit_max: { type: "number", min: 1, max: 100 },
          stability_max: { type: "number", min: 1, max: 100 },
          lifecycle_max: { type: "number", min: 1, max: 100 }
        },
        required_fields: ["performance_max", "regime_fit_max", "stability_max", "lifecycle_max"],
        total_must_equal: 100,
        tolerance: 0.01
      },
      has_user_override: false,
      display_unit: "points",
      unit_type: "score"
    }
  },
  timing: {
    review_intervals: {
      value: { default: 30, deactivate: 30, promote_demote: 14, adjust_allocation: 7 },
      default_value: { default: 30, deactivate: 30, promote_demote: 14, adjust_allocation: 7 },
      description: "Review intervals in days for different portfolio actions",
      validation_rules: {
        type: "object",
        properties: {
          default: { type: "number", min: 1, max: 365 },
          deactivate: { type: "number", min: 1, max: 365 },
          promote_demote: { type: "number", min: 1, max: 365 },
          adjust_allocation: { type: "number", min: 1, max: 365 }
        },
        required_fields: ["default", "deactivate", "promote_demote", "adjust_allocation"]
      },
      has_user_override: false,
      display_unit: "days",
      unit_type: "temporal"
    }
  }
};

const wizardSteps: WizardStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Portfolio Configuration',
    description: 'Let\'s set up your portfolio management preferences step by step.',
    category: 'intro',
    fields: [],
    icon: StarIcon
  },
  {
    id: 'lifecycle-allocation',
    title: 'Lifecycle Configuration',
    description: 'Configure trade count thresholds for lifecycle progression and maximum allocation percentages for each stage.',
    category: 'lifecycle',
    fields: ['trade_count_thresholds', 'allocation_caps'],
    icon: ShieldCheckIcon
  },
  {
    id: 'account-moves',
    title: 'Strategy Promotion & Demotion',
    description: 'Configure account hierarchy behavior and performance thresholds for strategy movements.',
    category: 'account_moves',
    fields: ['hierarchy_behavior', 'promotion_thresholds', 'demotion_thresholds'],
    icon: ChartBarIcon
  },
  {
    id: 'stability-controls',
    title: 'Stability & Risk Controls',
    description: 'Set deviation thresholds that trigger performance warnings and risk alerts.',
    category: 'stability',
    fields: ['deviation_thresholds'],
    icon: CogIcon
  },
  {
    id: 'summary',
    title: 'Review & Complete',
    description: 'Review your configuration choices and apply them to your portfolio.',
    category: 'summary',
    fields: [],
    icon: CheckIcon
  }
];

// Define the default hierarchy behavior that should be used across all presets
const DEFAULT_HIERARCHY_BEHAVIOR = {
  mode: "same_broker_only",
  enable_promotions: true,
  enable_demotions: true,
  enable_swaps: true,
  cross_broker_threshold: 2.0
};

const presetConfigurations = {
  conservative: {
    name: 'Conservative',
    description: 'Lower risk approach with minimal allocation to early-stage strategies and strict performance requirements',
    icon: '🛡️',
    configs: {
      lifecycle: {
        trade_count_thresholds: { initialization: 35, incubation: 60, maturity: 250 },
        allocation_caps: { initialization: 2, incubation: 3, maturity: 6, legacy: 12 }
      },
      account_moves: {
        promotion_thresholds: { Initialization: 0, Incubation: 0.8, Maturity: 1.5, Legacy: 2.0 },
        demotion_thresholds: { Initialization: -0.3, Incubation: -0.1, Maturity: -0.05, Legacy: 0.1 },
        hierarchy_behavior: DEFAULT_HIERARCHY_BEHAVIOR
      },
      stability: {
        deviation_thresholds: { win_rate_red: 8, win_rate_orange: 4, profit_factor_red: 25, profit_factor_orange: 15 }
      }
    }
  },
  balanced: {
    name: 'Balanced',
    description: 'Moderate risk with database default allocations and balanced performance expectations',
    icon: '⚖️',
    configs: {
      lifecycle: {
        trade_count_thresholds: { initialization: 30, incubation: 50, maturity: 200 },
        allocation_caps: { initialization: 3, incubation: 5, maturity: 8, legacy: 10 }
      },
      account_moves: {
        promotion_thresholds: { Initialization: 0, Incubation: 0.5, Maturity: 1, Legacy: 1.5 },
        demotion_thresholds: { Initialization: -0.5, Incubation: -0.2, Maturity: -0.1, Legacy: 0 },
        hierarchy_behavior: DEFAULT_HIERARCHY_BEHAVIOR
      },
      stability: {
        deviation_thresholds: { win_rate_red: 10, win_rate_orange: 5, profit_factor_red: 35, profit_factor_orange: 20 }
      }
    }
  },
  aggressive: {
    name: 'Aggressive',
    description: 'Higher risk tolerance with increased allocation to experimental strategies and relaxed performance thresholds',
    icon: '🚀',
    configs: {
      lifecycle: {
        trade_count_thresholds: { initialization: 25, incubation: 40, maturity: 150 },
        allocation_caps: { initialization: 5, incubation: 8, maturity: 12, legacy: 15 }
      },
      account_moves: {
        promotion_thresholds: { Initialization: 0, Incubation: 0.3, Maturity: 0.7, Legacy: 1.0 },
        demotion_thresholds: { Initialization: -0.8, Incubation: -0.4, Maturity: -0.2, Legacy: -0.1 },
        hierarchy_behavior: DEFAULT_HIERARCHY_BEHAVIOR
      },
      stability: {
        deviation_thresholds: { win_rate_red: 15, win_rate_orange: 8, profit_factor_red: 50, profit_factor_orange: 30 }
      }
    }
  }
};

export const ConfigWizard: React.FC<ConfigWizardProps> = ({
  isOpen,
  onClose,
  onComplete,
  currentConfigs = {}
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [wizardConfigs, setWizardConfigs] = useState<any>({});
  const [stepValidationErrors, setStepValidationErrors] = useState<Record<number, string[]>>({});
  const initializedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      setCurrentStep(0);
      setSelectedPreset(null);
      // Initialize with current configs or defaults
      if (currentConfigs && Object.keys(currentConfigs).length > 0) {
        setWizardConfigs(currentConfigs);
      } else {
        // Initialize with default values from configurationFields
        const initialValues: any = {};
        Object.entries(configurationFields).forEach(([category, fields]) => {
          initialValues[category] = {};
          Object.entries(fields).forEach(([fieldKey, fieldData]) => {
            initialValues[category][fieldKey] = fieldData.default_value;
          });
        });
        setWizardConfigs(initialValues);
      }
      initializedRef.current = true;
    } else if (!isOpen) {
      // Reset initialization flag when modal closes
      initializedRef.current = false;
    }
  }, [isOpen]);

  const handleWizardValueChange = (category: string, fieldKey: string, value: any) => {
    setWizardConfigs((prev: any) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [fieldKey]: value
      }
    }));
  };

  // Validate current step configuration
  const validateCurrentStep = (): ValidationResult[] => {
    const currentStepData = wizardSteps[currentStep];
    if (!currentStepData.fields || currentStepData.fields.length === 0) {
      return []; // No fields to validate (intro/summary steps)
    }

    const errors: ValidationResult[] = [];
    const category = currentStepData.category;
    
    if (!wizardConfigs[category]) {
      return [{ isValid: false, error: `Missing configuration for ${category}` }];
    }

    // Validate each field in the current step
    for (const fieldKey of currentStepData.fields) {
      const categoryConfig = configurationFields[category as keyof typeof configurationFields];
      if (!categoryConfig) continue;
      
      const fieldConfig = (categoryConfig as any)[fieldKey];
      if (!fieldConfig) continue;

      const currentValue = wizardConfigs[category][fieldKey];
      const validation = validateObjectField(currentValue, fieldConfig.validation_rules);
      
      if (!validation.isValid) {
        errors.push({
          isValid: false,
          error: `${fieldKey}: ${validation.error}`
        });
      }
    }

    return errors;
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (currentStep > 0 && currentStep < wizardSteps.length - 1) {
      const validationErrors = validateCurrentStep();
      if (validationErrors.length > 0) {
        setStepValidationErrors(prev => ({
          ...prev,
          [currentStep]: validationErrors.map(e => e.error || 'Validation error')
        }));
        return; // Don't proceed if validation fails
      } else {
        // Clear any previous errors for this step
        setStepValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[currentStep];
          return newErrors;
        });
      }
    }

    if (currentStep < wizardSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePresetSelect = (presetKey: string) => {
    setSelectedPreset(presetKey);
    const preset = presetConfigurations[presetKey as keyof typeof presetConfigurations];
    if (preset) {
      setWizardConfigs(preset.configs);
    }
  };

  const handleComplete = () => {
    onComplete(wizardConfigs);
    onClose();
  };

  const renderWelcomeStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <StarIcon className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Welcome to Portfolio Configuration</h2>
        <p className="text-white/70 text-lg max-w-2xl mx-auto">
          Configure your portfolio management settings with our guided wizard. 
          You can choose from preset configurations or customize each setting to match your preferences.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(presetConfigurations).map(([key, preset]) => (
          <motion.button
            key={key}
            onClick={() => handlePresetSelect(key)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${
              selectedPreset === key
                ? 'border-blue-400 bg-blue-400/10'
                : 'border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10'
            }`}
          >
            <div className="text-3xl mb-3">{preset.icon}</div>
            <h3 className="text-lg font-semibold text-white mb-2">{preset.name}</h3>
            <p className="text-white/70 text-sm">{preset.description}</p>
          </motion.button>
        ))}
      </div>

      {selectedPreset && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <p className="text-green-400 text-sm">
            ✓ {presetConfigurations[selectedPreset as keyof typeof presetConfigurations].name} preset selected
          </p>
        </motion.div>
      )}
    </motion.div>
  );

  const renderSummaryStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckIcon className="w-8 h-8 text-green-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Configuration Summary</h2>
        <p className="text-white/70">Review your settings before applying them</p>
      </div>

      <div className="space-y-4">
        {Object.entries(wizardConfigs).map(([category, configs]) => (
          <div key={category} className="bg-white/5 rounded-lg p-4 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-3 capitalize">
              {category.replace('_', ' ')}
            </h3>
            <div className="space-y-2">
              {Object.entries(configs as any).map(([fieldKey, value]) => (
                <div key={fieldKey} className="flex justify-between items-center text-sm">
                  <span className="text-white/70 capitalize">
                    {fieldKey.replace('_', ' ')}:
                  </span>
                  <span className="text-white font-mono">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  const renderConfigurationStep = () => {
    const step = wizardSteps[currentStep];
    const categoryConfig = configurationFields[step.category as keyof typeof configurationFields];
    
    if (!categoryConfig) {
      return (
        <div className="text-center py-8 text-white/60">
          <p>No configuration options available for this step.</p>
        </div>
      );
    }

    // Add educational content for lifecycle allocation step
    const renderLifecycleExplanation = () => {
      if (step.category === 'lifecycle' && (step.fields.includes('allocation_caps') || step.fields.includes('trade_count_thresholds'))) {
        // Get current allocation caps and trade count thresholds from wizard configs
        const allocationCaps = wizardConfigs?.lifecycle?.allocation_caps || 
          configurationFields.lifecycle.allocation_caps.default_value;
        const tradeThresholds = wizardConfigs?.lifecycle?.trade_count_thresholds || 
          configurationFields.lifecycle.trade_count_thresholds.default_value;
        
        return (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6 mb-6">
            <h3 className="text-white font-semibold mb-4 text-center">Understanding Lifecycle Progression:</h3>
            
            {/* Lifecycle Progression Flow */}
            <div className="flex flex-col lg:flex-row items-center justify-center space-y-4 lg:space-y-0 lg:space-x-4 mb-6">
              {/* Initialization */}
              <div className="flex flex-col items-center bg-white/5 rounded-lg p-4 border border-white/10 min-w-[140px]">
                <div className="text-3xl mb-2">🌱</div>
                <div className="text-white font-medium text-sm">Initialization</div>
                <div className="text-white/70 text-xs text-center mt-1">
                  &lt;{tradeThresholds?.initialization || 30} trades
                </div>
                <div className="text-blue-400 text-xs font-medium mt-1">
                  {allocationCaps?.initialization || 3}% max
                </div>
              </div>
              
              {/* Arrow */}
              <div className="flex items-center">
                <ArrowRightIcon className="w-6 h-6 text-white/60 transform lg:rotate-0 rotate-90" />
              </div>
              
              {/* Incubation */}
              <div className="flex flex-col items-center bg-white/5 rounded-lg p-4 border border-white/10 min-w-[140px]">
                <div className="text-3xl mb-2">🌿</div>
                <div className="text-white font-medium text-sm">Incubation</div>
                <div className="text-white/70 text-xs text-center mt-1">
                  {tradeThresholds?.initialization || 30}-{tradeThresholds?.incubation || 50} trades
                </div>
                <div className="text-green-400 text-xs font-medium mt-1">
                  {allocationCaps?.incubation || 5}% max
                </div>
              </div>
              
              {/* Arrow */}
              <div className="flex items-center">
                <ArrowRightIcon className="w-6 h-6 text-white/60 transform lg:rotate-0 rotate-90" />
              </div>
              
              {/* Maturity */}
              <div className="flex flex-col items-center bg-white/5 rounded-lg p-4 border border-white/10 min-w-[140px]">
                <div className="text-3xl mb-2">🌳</div>
                <div className="text-white font-medium text-sm">Maturity</div>
                <div className="text-white/70 text-xs text-center mt-1">
                  {tradeThresholds?.incubation || 50}-{tradeThresholds?.maturity || 200} trades
                </div>
                <div className="text-yellow-400 text-xs font-medium mt-1">
                  {allocationCaps?.maturity || 8}% max
                </div>
              </div>
              
              {/* Arrow */}
              <div className="flex items-center">
                <ArrowRightIcon className="w-6 h-6 text-white/60 transform lg:rotate-0 rotate-90" />
              </div>
              
              {/* Legacy */}
              <div className="flex flex-col items-center bg-white/5 rounded-lg p-4 border border-white/10 min-w-[140px]">
                <div className="text-3xl mb-2">🏛️</div>
                <div className="text-white font-medium text-sm">Legacy</div>
                <div className="text-white/70 text-xs text-center mt-1">
                  &gt;{tradeThresholds?.maturity || 200} trades
                </div>
                <div className="text-purple-400 text-xs font-medium mt-1">
                  {allocationCaps?.legacy || 10}% max
                </div>
              </div>
            </div>
            
            {/* Additional Context */}
            <div className="text-sm text-white/70 text-center bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="mb-2">
                <span className="text-white font-medium">Strategy Progression:</span> Strategies automatically advance through lifecycle stages based on trade count and performance.
              </p>
              <p>
                <span className="text-white font-medium">Trade Thresholds:</span> Set the minimum number of trades required before a strategy can progress to the next stage.
              </p>
              <p>
                <span className="text-white font-medium">Allocation Caps:</span> Higher caps for mature strategies reduce portfolio risk while allowing growth.
              </p>
            </div>
          </div>
        );
      }
      return null;
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <step.icon className="w-8 h-8 text-white/80" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{step.title}</h2>
          <p className="text-white/70">{step.description}</p>
        </div>

        {renderLifecycleExplanation()}

        <div className="space-y-4">
          {Object.entries(categoryConfig).map(([fieldKey, fieldData]) => (
            <NestedConfigField
              key={fieldKey}
              fieldKey={fieldKey}
              fieldData={{
                value: wizardConfigs[step.category]?.[fieldKey] || fieldData.default_value,
                default_value: fieldData.default_value,
                description: fieldData.description,
                validation_rules: fieldData.validation_rules,
                has_user_override: false
              }}
              onValueChange={(_path: string, value: any) => {
                handleWizardValueChange(step.category, fieldKey, value);
              }}
              onRemoveOverride={() => {}}
              categoryKey={step.category}
            />
          ))}
        </div>
      </motion.div>
    );
  };

  const renderStepContent = () => {
    const step = wizardSteps[currentStep];
    
    if (step.id === 'welcome') {
      return renderWelcomeStep();
    }
    
    if (step.id === 'summary') {
      return renderSummaryStep();
    }

    return renderConfigurationStep();
  };

  if (!isOpen) return null;

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === wizardSteps.length - 1;

  const modalContent = (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-gray-900/95 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden my-8"
        >
          {/* Header */}
          <div className="p-6 border-b border-white/20">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-white">Portfolio Configuration Wizard</h1>
              <button
                onClick={onClose}
                className="text-white/60 hover:text-white/80 transition-colors"
              >
                ✕
              </button>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-white/60 mb-2">
                <span>Step {currentStep + 1} of {wizardSteps.length}</span>
                <span>{Math.round(((currentStep + 1) / wizardSteps.length) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2">
                <motion.div
                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentStep + 1) / wizardSteps.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 min-h-[400px] max-h-[calc(90vh-200px)] overflow-y-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Validation Errors */}
          {stepValidationErrors[currentStep] && stepValidationErrors[currentStep].length > 0 && (
            <div className="px-6 py-4 bg-red-500/10 border-t border-red-500/20">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-400">Please fix the following validation errors:</h3>
                  <ul className="mt-2 space-y-1">
                    {stepValidationErrors[currentStep].map((error, index) => (
                      <li key={index} className="text-sm text-red-300">• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-6 border-t border-white/20">
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrevious}
                disabled={isFirstStep}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <ChevronLeftIcon className="w-4 h-4 mr-2" />
                Previous
              </button>

              <div className="flex items-center space-x-2">
                {wizardSteps.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index <= currentStep ? 'bg-blue-400' : 'bg-white/20'
                    }`}
                  />
                ))}
              </div>

              {isLastStep ? (
                <button
                  onClick={handleComplete}
                  className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-all duration-200"
                >
                  <CheckIcon className="w-4 h-4 mr-2" />
                  Apply Configuration
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!selectedPreset && isFirstStep}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Next
                  <ChevronRightIcon className="w-4 h-4 ml-2" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default ConfigWizard;